import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut, Menu, PhoneCall, Search, Sparkles, X } from 'lucide-react';
import { useTenant } from '../../lib/tenant-context.js';
import { useAuth } from '../../hooks/useAuth.js';
import {
  buildMobileNavSections,
  currentMobileNavTitle,
  isMobileNavRouteActive,
} from '../../lib/mobileNavModel.js';
import ThemeToggle from '../ui/theme-toggle.js';
import PrivacyToggle from '../ui/privacy-toggle.js';
import GlobalDialer from '../calls/GlobalDialer.js';
import { BOTTOM_ITEMS, NAV_ITEMS, type NavItem } from './nav-rail.js';
import VacationIndicator from './VacationIndicator.js';

interface MobileNavProps {
  onOpenCommandPalette: () => void;
  onToggleNeo: () => void;
  neoOpen: boolean;
  mobileContextAction?: React.ReactNode;
}

function MobileNavLink({ item, active, onSelect }: { item: NavItem; active: boolean; onSelect: () => void }) {
  const Icon = item.icon;
  return (
    <Link href={item.href}>
      <motion.div
        whileTap={{ scale: 0.98 }}
        onClick={onSelect}
        className={[
          'flex min-h-12 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors',
          active
            ? 'bg-selected text-selected-fg'
            : 'bg-surface-raised text-ink hover:bg-surface-sunken',
        ].join(' ')}
        aria-current={active ? 'page' : undefined}
      >
        <Icon size={18} className="shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.shortcut && <span className="text-[10px] opacity-70">{item.shortcut}</span>}
      </motion.div>
    </Link>
  );
}

export default function MobileNav({ onOpenCommandPalette, onToggleNeo, neoOpen, mobileContextAction }: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [location] = useLocation();
  const { tenant } = useTenant();
  const { logout } = useAuth();
  const sections = useMemo(() => buildMobileNavSections(NAV_ITEMS, BOTTOM_ITEMS), []);
  const title = currentMobileNavTitle(location, [...NAV_ITEMS, ...BOTTOM_ITEMS]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <>
      <header
        className="safe-top-bar fixed inset-x-0 top-0 z-modal flex items-center gap-2 border-b border-line bg-surface/95 px-3 shadow-sm backdrop-blur md:hidden"
        style={{ height: 'calc(3.5rem + env(safe-area-inset-top, 0px))' }}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-raised text-ink shadow-sm ring-1 ring-line"
          aria-label="Open navigation"
          aria-expanded={open}
        >
          <Menu size={20} aria-hidden />
        </button>

        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
            {tenant?.brandName || 'POE'}
          </div>
          <div className="truncate text-sm font-semibold text-ink">{title}</div>
        </div>

        <VacationIndicator compact />
        {mobileContextAction}
        <button
          type="button"
          onClick={() => setDialerOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-raised text-ink-muted shadow-sm ring-1 ring-line"
          aria-label="Open dialer"
        >
          <PhoneCall size={18} aria-hidden />
        </button>
        <button
          type="button"
          onClick={onOpenCommandPalette}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-raised text-ink-muted shadow-sm ring-1 ring-line"
          aria-label="Search POE"
        >
          <Search size={18} aria-hidden />
        </button>
        <button
          type="button"
          onClick={onToggleNeo}
          className={[
            'flex h-10 w-10 items-center justify-center rounded-xl shadow-sm ring-1 ring-line',
            neoOpen ? 'bg-selected text-selected-fg' : 'bg-surface-raised text-ink-muted',
          ].join(' ')}
          aria-label={neoOpen ? 'Close Neo chat' : 'Open Neo chat'}
          aria-pressed={neoOpen}
        >
          <Sparkles size={18} aria-hidden />
        </button>
      </header>

      <GlobalDialer open={dialerOpen} onClose={() => setDialerOpen(false)} />

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-modal-overlay bg-ink/45 backdrop-blur-sm md:hidden"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="fixed bottom-0 left-0 top-0 z-modal flex w-[min(86vw,340px)] flex-col border-r border-line bg-surface p-4 shadow-2xl md:hidden"
              style={{
                paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))',
                paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
              }}
              role="navigation"
              aria-label="Mobile navigation"
            >
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-fg shadow-sm">
                  <span className="font-bold">{(tenant?.brandName?.[0] || 'P').toUpperCase()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-base font-semibold text-ink">{tenant?.brandName || 'POE'}</div>
                  <div className="text-xs text-ink-muted">Outreach workbench</div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-ink-muted hover:bg-surface-sunken hover:text-ink"
                  aria-label="Close navigation"
                >
                  <X size={20} aria-hidden />
                </button>
              </div>

              <div className="flex-1 space-y-5 overflow-y-auto">
                {sections.map((section) => (
                  <section key={section.title}>
                    <h2 className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                      {section.title}
                    </h2>
                    <div className="space-y-1.5">
                      {section.items.map((item) => (
                        <MobileNavLink
                          key={item.href}
                          item={item}
                          active={isMobileNavRouteActive(item.href, location, item.aliases)}
                          onSelect={() => setOpen(false)}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-4 border-t border-line pt-4">
                <div className="flex items-center justify-between rounded-xl bg-surface-raised px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-ink">Appearance</div>
                    <div className="text-xs text-ink-muted">Light, dark, earth, or system</div>
                  </div>
                  <ThemeToggle />
                </div>
                <div className="mt-2 flex items-center justify-between rounded-xl bg-surface-raised px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-ink">Privacy</div>
                    <div className="text-xs text-ink-muted">Blur sensitive data for screen-sharing</div>
                  </div>
                  <PrivacyToggle />
                </div>
                <button
                  type="button"
                  onClick={() => { setOpen(false); logout(); }}
                  className="mt-2 flex min-h-12 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium text-ink-muted transition-colors hover:bg-danger/10 hover:text-danger"
                  aria-label="Log out"
                  data-testid="button-logout-mobile"
                >
                  <LogOut size={18} className="shrink-0" aria-hidden />
                  <span>Log out</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
